import React from "react";
import { SheetSettledProvider } from "@/components/ui/sheetSettled";
import { useSheetSettled } from "@/hooks/use-sheet-settled";
import { useTranslation } from "react-i18next";
import { Dialog } from "@/components/ui/dialog";
import { ProtectedDialogContent } from "@/components/ui/ProtectedDialogContent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  SheetCloseButton,
  SHEET_HEADER_CLASS,
  SHEET_BODY_CLASS,
  SHEET_FOOTER_CLASS,
  FORM_SHEET_H_CLASS,
  FORM_SHEET_H_TALL_CLASS,
  type SheetFit,
} from "@/components/ui/sheetChrome";
import { useSheetFitClass } from "@/hooks/use-sheet-fit";
import { DialogVeilProvider, useDialogVeilHost, useVeilAncestor } from "@/components/ui/dialogVeil";
import { SheetMoreMenu } from "@/components/ui/SheetMoreMenu";

/**
 * Unified bottom-sheet/modal shell for all create/edit forms across the app.
 *
 * - Teléfono: hoja inferior pegada al borde, a todo el ancho, con el tirador
 *   canónico (arrastrar para cerrar) y la zona segura respetada. Antes tenía dos
 *   aspectos según `flush`: pegada abajo, o flotando con 8 px de margen y las
 *   cuatro esquinas redondeadas. Solo tres de la cincuentena de formularios
 *   pasaban `flush`, así que casi todos flotaban.
 * - Desktop/tablet: centered modal, max-w-[480px] md / [520px] lg, max-h-[88vh].
 * - `size="tall"` locks to full available height (used by stepper wizards).
 * - Dirty-close confirmation built in.
 * - Optional sticky footer slot (pass action buttons via `footer`).
 */
export interface FormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  /**
   * Emblema que acompaña al título. Va DENTRO del `<h2>`, no al lado: como
   * elemento hermano en la fila flex se centraría verticalmente contra el bloque
   * entero y, con un título de dos líneas, acabaría flotando en mitad del hueco.
   * Metido en el flujo del texto se queda pegado a la última palabra, caiga donde
   * caiga el corte de línea.
   */
  titleIcon?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Whether the form has unsaved input; gates the X-close confirmation. */
  isDirty?: boolean;
  /**
   * When true, the `subtitle` is hidden from the header and instead revealed
   * through a small info (ℹ️) button rendered next to the title — clicking it
   * pops the subtitle up in a compact box. Off by default: every other sheet
   * keeps showing its subtitle inline underneath the title.
   */
  subtitleAsInfo?: boolean;
  /**
   * Botón que va a la IZQUIERDA del título, en la misma fila que la X (lo normal:
   * la flecha de volver, con `SheetBackButton`).
   *
   * Existe porque la alternativa era meterlo dentro del `title`, y ahí no es un
   * botón de la cabecera: es parte del texto. Con un título de dos renglones baja
   * con el corte de línea y se queda a media altura, enfrente de una X que sigue
   * arriba. Aquí es hermano de la X, así que los dos miden y se alinean igual.
   */
  leadingAction?: React.ReactNode;
  /**
   * Las líneas del «⋯» que va a la IZQUIERDA de la X (26-09-2026): los
   * `SheetMoreMenuItem` de lo secundario de esta hoja («Cambiar maquinaria»,
   * «Archivar»…). El botón lo pone esta carcasa con `SheetMoreMenu`, así que es
   * el mismo en todas; sin la prop no se pinta nada y la cabecera queda como
   * siempre. Abrirlo no dispara la confirmación de descarte (U7).
   */
  trailingMenu?: React.ReactNode;
  /** Sticky footer content (typically a row of action buttons). */
  footer?: React.ReactNode;
  /**
   * EL CUERPO NO SE MONTA MIENTRAS LA HOJA SUBE (29-08-2026).
   *
   * Pasando `skeleton` (o `loading`), la hoja enseña ese hueco hasta que se ha
   * posado y solo entonces monta a sus hijos. Es lo que separa una hoja que
   * «abre suave» de una que entra a tirones: no se puede animar lo que todavía
   * no existe, así que todo lo que haya dentro se construye ANTES de que la
   * hoja pueda moverse, y en un Android de gama baja eso son fotogramas
   * perdidos justo al principio.
   *
   * `loading` es para cuando además faltan DATOS: mantiene el esqueleto después
   * de la subida. NO retrasa la consulta —eso sería perder red a cambio de
   * nada—: la petición sale al abrir, como siempre, y lo único que espera es el
   * momento de pintarla. El razonamiento largo está en `use-sheet-settled.ts`.
   */
  skeleton?: React.ReactNode;
  /** Los datos todavía no están: el esqueleto sigue puesto después de subir. */
  loading?: boolean;
  /** Form body. */
  children: React.ReactNode;
  /** Optional override className on the body wrapper. */
  bodyClassName?: string;
  /** Optional override classes on the dialog panel (e.g. a narrower max-width). */
  contentClassName?: string;
  /**
   * "auto" (default): sheet hugs its content height up to the cap.
   * "tall": sheet always uses full cap height (use for multi-step wizards with stepper).
   */
  size?: "auto" | "tall";
  /**
   * When this value changes, the scrollable body resets to the top. Pass the
   * current step index for multi-step wizards so each new step starts at the top.
   */
  scrollResetKey?: string | number;
  /**
   * Cuánto ocupa la hoja en el TELÉFONO. Ver `useSheetFit`.
   *
   * Por defecto `page`, y no `auto`: las cuarenta y cuatro pantallas que montan
   * esta carcasa son formularios y listas, o sea SITIOS A LOS QUE SE ENTRA. Con
   * `auto` cada una medía su contenido y salían de nueve alturas distintas —
   * «Añadir ubicación» son dos campos y abría una tira de tres dedos pegada al
   * borde de abajo, que no parece una pantalla, parece un aviso que se ha colado
   * por la esquina—. Las confirmaciones de verdad («¿seguro que borras?») no
   * pasan por aquí: son AlertDialog, una tarjeta centrada.
   *
   * - `page` (lo normal): abre a un alto cómodo aunque lleve poco, y crece.
   * - `auto`: mide y ya. Para las que ya están bien midiendo su contenido.
   * - `expanded`: alto FIJO a pantalla completa. Para asistentes de varios pasos,
   *   que no deben cambiar de tamaño entre uno y otro.
   * - `short`: nunca crece. Para las que tapar la pantalla les quita el sentido,
   *   y para las que llevan un alto propio decidido a mano.
   *
   * En tableta y escritorio no hace nada: allí es una ventana centrada.
   */
  fit?: SheetFit;
  /**
   * UNA VENTANA A LA VEZ: mientras algo de dentro se abra ENCIMA, esta hoja se
   * aparta en vez de asomar por los bordes. Ver `dialogVeil`.
   *
   * Es opt-in: quien lo pide reparte el contexto a lo que lleve dentro, y lo de
   * dentro se apunta llamando a `useVeilAncestor`.
   */
  veilable?: boolean;
  /**
   * Apartarla a mano, sin pasar por el contexto. Para cuando quien abre la
   * ventana de encima es la propia pantalla que monta la hoja (el escáner abre
   * sus avisos como hermanos del panel, no como hijos).
   */
  veiled?: boolean;
  /**
   * @deprecated Alias de `fit="expanded"`, que es lo mismo dicho mejor. Se
   * mantiene porque el conmutador de voz y el parte de plagas ya lo pasaban.
   */
  expanded?: boolean;
  /**
   * @deprecated Ya no hace nada: ahora TODAS las hojas van pegadas al borde
   * inferior. Se sigue aceptando para no tocar los tres formularios que lo
   * pasaban.
   */
  flush?: boolean;
  /**
   * VENTANA CENTRADA TAMBIÉN EN EL TELÉFONO (27-09-2026), con aire alrededor, sin
   * tirador y con la X arriba a la derecha, como `GlassSheet centered`. Para los
   * diálogos que se abren desde una lista de botones (la página «📒 Cuaderno de
   * campo»). Sin la prop manda `FormSheetCentrado` (un contexto: así las hojas de
   * siempre que se abren desde allí salen centradas sin tocarlas), y sin él, la
   * hoja inferior de siempre en el teléfono.
   */
  centered?: boolean;
}

/**
 * «Todo FormSheet que se abra desde aquí dentro, centrado» (27-09-2026). Lo pone
 * la página del cuaderno alrededor de sus diálogos, y alcanza también a las hojas
 * que reutiliza sin tocarlas (máquina, carné, empresa de servicios, SIGPAC) y a
 * las que se abren ENCIMA de un diálogo (el contexto cruza los portales).
 */
const FormSheetCentradoContext = React.createContext(false);
export function FormSheetCentrado({ children }: { children: React.ReactNode }) {
  return <FormSheetCentradoContext.Provider value>{children}</FormSheetCentradoContext.Provider>;
}

export function FormSheet({
  open,
  onOpenChange,
  title,
  titleIcon,
  subtitle,
  subtitleAsInfo,
  isDirty,
  leadingAction,
  trailingMenu,
  footer,
  children,
  bodyClassName,
  contentClassName,
  size = "auto",
  scrollResetKey,
  fit = "page",
  skeleton,
  loading = false,
  veilable = false,
  veiled = false,
  expanded = false,
  // API heredada, ya sin efecto — ver el JSDoc de `flush`.
  flush: _flush,
  centered: centeredProp,
}: FormSheetProps) {
  const { t } = useTranslation();
  const centeredCtx = React.useContext(FormSheetCentradoContext);
  const centered = centeredProp ?? centeredCtx;
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  /** ¿Ha terminado ya de subir? Ver la prop `skeleton`. */
  const settled = useSheetSettled(open);

  /* Los dos lados del velo. Como HUÉSPED reparte el contexto a su cuerpo, para
     que lo que abra algo encima la aparte; como HIJA se apunta al huésped de
     más arriba mientras está abierta, y así una cadena de tres hojas deja ver
     solo la última. */
  const { veiled: veiledByChild, host } = useDialogVeilHost();
  useVeilAncestor(open);
  const isVeiled = veiled || (veilable && veiledByChild);

  const bodyRef = React.useRef<HTMLDivElement>(null);
  /* Además de la ref (que sirve para devolver el scroll arriba), el nodo en
     estado: montarse es lo que dispara la medida. Ver `useSheetFit`. */
  const [bodyNode, setBodyNode] = React.useState<HTMLDivElement | null>(null);
  const setBody = React.useCallback((node: HTMLDivElement | null) => {
    bodyRef.current = node;
    setBodyNode(node);
  }, []);

  // Reset scroll to the top whenever the step (scrollResetKey) changes, so a new
  // wizard step never opens mid-scroll with its first fields hidden.
  React.useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [scrollResetKey]);

  const handleClose = () => {
    if (isDirty) setConfirmOpen(true);
    else onOpenChange(false);
  };

  // Los dos tamaños miden su contenido (y se desplazan al pasarse del tope) en
  // vez de ser altos fijos; «tall» solo sube un poco el techo de escritorio para
  // los asistentes largos. Las clases viven en sheetChrome: aquí no se escribe
  // ninguna altura.
  // Centrada, el alto lo pone SHEET_CENTERED_PHONE_CLASS (en ProtectedDialogContent).
  const heightClasses = centered ? null : size === "tall" ? FORM_SHEET_H_TALL_CLASS : FORM_SHEET_H_CLASS;

  // Cuánto crece en el teléfono. `expanded` es el alias viejo de fit="expanded".
  const fitClass = useSheetFitClass({
    // Centrada = ventana flotante, no hoja: crecer no significa nada ahí.
    fit: centered ? "short" : expanded ? "expanded" : fit,
    host: bodyNode,
    resetKey: scrollResetKey,
  });

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(true))}>
        <ProtectedDialogContent
          isDirty={!!isDirty}
          veiled={isVeiled}
          /* El alto lo decide ESTA carcasa (mide su propio cuerpo), así que la de
             debajo no vuelve a medir el panel: `short` es «no toques el alto». */
          fit="short"
          centered={centered}
          hideCloseButton
          onDiscard={() => onOpenChange(false)}
          className={cn(
            // Colocarla abajo, el redondeo, el velo y la subida ya los pone
            // ProtectedDialogContent; aquí solo el relleno propio (esta carcasa
            // maneja cabecera, cuerpo y pie por su cuenta) y el ancho de la
            // ventana de escritorio.
            "!p-0 !gap-0 flex flex-col bg-white",
            "overflow-hidden overscroll-contain",
            "md:max-w-[480px] lg:max-w-[520px] md:w-[calc(100vw-3rem)]",
            heightClasses,
            contentClassName,
            /* AL FINAL, después del `contentClassName` del consumidor: las dos
               son del mismo grupo para tailwind-merge y gana la última. Puesto
               antes, un `max-md:!h-[62dvh]` de una pantalla concreta (la cadena
               de entrada de stock) se comía la decisión de la carcasa y la hoja
               se quedaba a medias sin que nada fallara.
               SIN `SHEET_GROW_CLASS` a propósito: esta carcasa mide `h-auto`, y
               una transición de `auto` a un alto fijo no la anima ningún
               navegador — quedaría el código diciendo que hay animación donde
               solo hay un salto, y encima saltándose «reducir movimiento». */
            fitClass,
          )}
        >
          {/* Header — `pt-7` en el teléfono para dejar sitio al tirador, que va
              en absoluto por encima. */}
          <div className={cn(SHEET_HEADER_CLASS, "pb-3 shrink-0", !centered && "max-md:pt-7")}>
            <div
              className={cn(
                "flex items-start justify-between gap-3",
                /* Con flecha de VOLVER, en el TELÉFONO el título baja a su propia
                   fila DEBAJO de la de la flecha y la X, en vez de apretujarse
                   entre las dos (donde queda a la altura del tirador y pegado a los
                   botones). La flecha y la X se quedan arriba (justify-between: una
                   a cada lado) y el `flex-wrap` + el `flex-[0_0_100%]` del título lo
                   empujan al siguiente renglón. Solo `max-md`: de tableta en
                   adelante la cabecera cabe de sobra y se queda en una sola fila. */
                !!leadingAction && "max-md:flex-wrap",
              )}
            >
              {leadingAction}
              <div
                className={cn(
                  "min-w-0 flex-1",
                  !!leadingAction && "max-md:order-last max-md:!flex-[0_0_100%]",
                )}
              >
                <h2 className="text-2xl font-bold tracking-tight text-foreground min-w-0">
                  {title}
                  {titleIcon && (
                    /* Detrás del título, pegado a la ÚLTIMA palabra: así el emblema
                       cierra la frase en lugar de abrirla, y con un título de dos
                       líneas acompaña al corte en vez de quedarse arriba solo.
                       `align-middle` lo centra contra el renglón; nada de `block`,
                       que lo sacaría del flujo del texto. */
                    <span className="inline-flex align-middle ms-2">{titleIcon}</span>
                  )}
                  {subtitle && subtitleAsInfo && (
                    /* El botón de info va INLINE, pegado a la última letra del
                       título y en el mismo renglón. */
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label={t("common.whatIsThis")}
                          className="align-middle ms-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                        >
                          <Info className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className="w-72 max-w-[calc(100vw-2rem)] text-sm text-muted-foreground leading-relaxed"
                      >
                        {subtitle}
                      </PopoverContent>
                    </Popover>
                  )}
                </h2>
                {subtitle && !subtitleAsInfo && (
                  <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
                )}
              </div>
              {trailingMenu ? (
                /* El «⋯» y la X van JUNTOS en un grupo: con la flecha de volver,
                   el `justify-between` de arriba deja la flecha a un lado y este
                   par al otro, y el título baja a su renglón sin separarlos. */
                <div className="flex items-center gap-1.5 shrink-0">
                  <SheetMoreMenu testId="sheet-more-menu">{trailingMenu}</SheetMoreMenu>
                  <SheetCloseButton onClick={handleClose} aria-label={t("common.close")} />
                </div>
              ) : (
                <SheetCloseButton onClick={handleClose} aria-label={t("common.close")} />
              )}
            </div>
          </div>

          {/* Scrollable body */}
          <div
            ref={setBody}
            className={cn(
              "flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain",
              SHEET_BODY_CLASS,
              /* SIN PIE, el hueco de la barra de Android va aquí. No puede ir
                 en el panel: esta hoja le pone `!p-0`, que es `padding: 0
                 !important` y gana al `pb-[max(...)]` de
                 ProtectedDialogContent, que no lleva `!important`. Con eso, la
                 última línea de las hojas de informe quedaba tapada por los
                 tres botones y no había forma de bajarla (29-08-2026). */
              !footer && "pb-[max(18px,var(--safe-bottom))]",
              // Centrada no toca el borde de abajo: no hay barra que esquivar.
              centered && !footer && "pb-[18px]",
              bodyClassName,
            )}
            style={{ touchAction: "pan-y" }}
          >
            {(() => {
              /* El esqueleto sustituye al cuerpo, no se le superpone: montar los
                 hijos y taparlos sería pagar el coste que se quiere evitar. */
              const cuerpo =
                skeleton && (loading || !settled) ? (
                  skeleton
                ) : veilable ? (
                  <DialogVeilProvider value={host}>{children}</DialogVeilProvider>
                ) : (
                  children
                );
              /* Y se reparte «ya he subido» hacia dentro: un cuerpo que es un
                 componente hondo puede diferirse solo con `<DeferUntilSettled>`
                 sin que esta carcasa sepa nada de él. */
              return <SheetSettledProvider open={open}>{cuerpo}</SheetSettledProvider>;
            })()}
          </div>

          {/* Sticky footer */}
          {footer && (
            <div className={cn(SHEET_FOOTER_CLASS, "shrink-0 border-t bg-white flex items-center gap-3", centered && "pb-3")}>
              {footer}
            </div>
          )}
        </ProtectedDialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.discardChangesTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.discardChangesDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                onOpenChange(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.discardConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Standard primary action button styled like the Crear Tarea CTA
 * (teal→blue gradient, 12px-tall, full-width on mobile).
 * Use inside FormSheet.footer for the main submit/save button.
 */
export function FormSheetPrimaryButton({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "flex-1 h-12 px-4 rounded-2xl font-semibold text-sm text-white",
        "bg-[#2E84DE]",
        "hover:bg-[#2E84DE]/90 shadow-[0_10px_24px_-10px_rgba(46,132,222,0.6)]",
        "disabled:opacity-50 disabled:bg-muted disabled:bg-none disabled:text-muted-foreground disabled:shadow-none",
        "transition-opacity inline-flex items-center justify-center gap-2 text-center leading-tight",
        className,
      )}
    />
  );
}

/**
 * Standard secondary action button (outline, matches Crear Tarea "Atrás").
 */
export function FormSheetSecondaryButton({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "h-12 px-4 rounded-2xl border border-[#b6dded] bg-white text-foreground text-sm",
        "hover:bg-muted/40 transition-colors font-medium inline-flex items-center justify-center text-center leading-tight",
        className,
      )}
    />
  );
}

/**
 * Destructive ghost text button (red, no border). Used for "Skip for now"
 * or other low-emphasis destructive actions inside FormSheet footers.
 */
export function FormSheetGhostDestructiveButton({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "h-12 px-4 rounded-2xl font-medium text-destructive",
        "hover:bg-destructive/10 transition-colors",
        "disabled:opacity-50",
        className,
      )}
    />
  );
}
